import React, { useEffect, useRef, useState } from "react";
import { Col, Button, Input } from "reactstrap";

import "./Style.css";
import { savePremiumCalculatorEnquiry } from "../../../../api";
import { toast } from "react-toastify";
import ShareQuoteModal from "./ShareQuoteModal";
import InsuranceBenefit from "../policy-create/insurance-benefit-modal";
import planLoader from "../../../../assets/images/Plan_Loader.gif";

const toTwo = (value) => Number((Number(value) || 0).toFixed(2));

/**
 * MembershipFee – a simplified plan listing that shows:
 *   Plan Cost → GST (18%) → Total Cost
 * No add-ons, no discounts, no insurance dropdowns.
 */
const MembershipFee = ({
    plans = [],
    planCount = 0,
    currentPage = 1,
    onPageChange = () => {},
    showBackButton = true,
    activeSearchQuery = "",
    onSearchPlans = () => {},
    isLoadingPlans = false,
    selectedPlan,
    selectedPlanDetails = {},
    handleSelectPlan = () => {},
    handleCoverChange = () => {},
    policyData = {},
    enquiryData = {},
    onShareQuoteSuccess = () => {},
    onBack = () => {},
}) => {
    const isMountedRef = useRef(true);
    const [searchInput, setSearchInput] = useState(activeSearchQuery || "");
    const [isShareQuoteOpen, setIsShareQuoteOpen] = useState(false);
    const [shareQuoteSubmitting, setShareQuoteSubmitting] = useState(false);
    const [coveragesPricingArray, setCoveragesPricingArray] = useState([]);
    const [isViewInsuranceBenefit, setViewInsuranceBenefit] = useState(false);
    const [insuranceBenefitsData, setInsuranceBenefitsData] = useState([]);
    const [selectedPlansForShare, setSelectedPlansForShare] = useState([]);
    // Remembers the real last page once we receive a non-full or empty page,
    // so the total never exceeds what the data actually has.
    const [knownLastPage, setKnownLastPage] = useState(null);

    const plansPerPage = 12;
    const totalPages = knownLastPage !== null
        ? knownLastPage
        : Math.max(1, Math.ceil(planCount / plansPerPage));

    useEffect(() => {
        isMountedRef.current = true;

        return () => {
            isMountedRef.current = false;
        };
    }, []);

    /* ── keep search input in sync when parent resets query ── */
    useEffect(() => {
        setSearchInput(activeSearchQuery || "");
        // Reset discovered last page on new search so it recalculates fresh.
        setKnownLastPage(null);
    }, [activeSearchQuery]);

    /* ── discover the real last page from actual data length ── */
    useEffect(() => {
        if (isLoadingPlans) return;
        const count = Array.isArray(plans) ? plans.length : 0;
        if (count < plansPerPage) {
            // This page is non-full (including 0) — it is the last page.
            setKnownLastPage((prev) =>
                prev === null || currentPage < prev ? currentPage : prev
            );
        }
    }, [plans, currentPage, isLoadingPlans]);

    /* ── guard page out of range ── */
    useEffect(() => {
        if (currentPage > totalPages) {
            onPageChange(totalPages);
        }
    }, [currentPage, totalPages, onPageChange]);

    useEffect(() => {
        if (!Array.isArray(plans) || plans.length === 0) {
            return;
        }

        setSelectedPlansForShare((prev) =>
            prev.map((selectedPlanItem) => {
                const latestPlan = plans.find((plan) => plan.id === selectedPlanItem.id);
                return latestPlan || selectedPlanItem;
            })
        );
    }, [plans]);

    /* ── build simple coverage pricing array for ShareQuote payload ── */
    useEffect(() => {
        if (!selectedPlan) {
            setCoveragesPricingArray([]);
            return;
        }
        const plan = plans.find((p) => p.id === selectedPlan);
        if (!plan || !Array.isArray(plan.plan_costs)) {
            setCoveragesPricingArray([]);
            return;
        }
        const result = plan.plan_costs.map((costOption, index) => {
            const planCost = toTwo(costOption.cost || 0);
            const gstAmount = toTwo(planCost * 0.18);
            const totalCost = toTwo(planCost + gstAmount);
            return { coverageIndex: index, cover: costOption.cover, planCost, gstAmount, totalCost };
        });
        setCoveragesPricingArray(result);
    }, [selectedPlan, plans]);

    /* ── search ── */
    const handleSearch = () => {
        onSearchPlans(searchInput);
    };

    /* ── view insurance benefits ── */
    const handleViewInsuranceBenefits = (plan) => {
        if (!plan || !Array.isArray(plan.plan_service_alloweds)) {
            return;
        }
        // const benefitsData = plan.plan_service_alloweds.map((item) => ({
        //     service: { title: item.service?.title || item.service, id: item.service?.id },
        //     sum_assured: item.sum_assured,
        //     deductible: item.deductible,
        // }));
        setInsuranceBenefitsData(plan?.plan_has_services || []);
        setViewInsuranceBenefit(true);
    };

    /* ── share quote ── */
    const selectedPlanIds = selectedPlansForShare.map((plan) => plan.id);
    const sharePlan = selectedPlansForShare[0] || null;

    const getCoverageSymbol = (plan, coverValue) => {
        const matchedMedicalCover = plan?.plan_medical_covers?.find(
            (item) => String(item.cover) === String(coverValue)
        );

        return (
            matchedMedicalCover?.currency?.symbol ||
            (policyData?.generateQuote?.geographical_cover?.toLowerCase() === "schengen"
                ? "€"
                : "$")
        );
    };

    const buildCoveragePricingDetails = (plan) => {
        const planCosts = Array.isArray(plan?.plan_costs) ? plan.plan_costs : [];

        return {
            plan_id: plan?.id || null,
            plan_name: plan?.title || "",
            coverages: planCosts.map((costOption) => {
                const planCost = toTwo(costOption?.cost || 0);
                const gstAmount = toTwo(planCost * 0.18);
                const planCostWithGST = toTwo(planCost + gstAmount);

                return {
                    cover: costOption?.cover,
                    coverage_symbol: getCoverageSymbol(plan, costOption?.cover),
                    plan_cost_with_gst: planCostWithGST,
                };
            }),
        };
    };

    const handleTogglePlanSelection = (
        plan,
        currentCover,
        totalCost,
        planCost,
    ) => {
        const isAlreadySelected = selectedPlanIds.includes(plan.id);

        if (isAlreadySelected) {
            setSelectedPlansForShare((prev) => prev.filter((item) => item.id !== plan.id));
            return;
        }

        if (selectedPlansForShare.length >= 3) {
            toast.error("You can select a maximum of 3 plans.");
            return;
        }

        setSelectedPlansForShare((prev) => [...prev, plan]);
        handleSelectPlan(
            plan.id,
            plan.title,
            currentCover,
            totalCost,
            planCost,
            0,
            planCost,
            null,
            null,
            null,
        );
    };

    const handleOpenShareQuote = () => {
        if (selectedPlansForShare.length < 1) {
            toast.error("Please select at least 1 plan.");
            return;
        }
        setIsShareQuoteOpen(true);
    };

    const handleCloseShareQuote = () => {
        setIsShareQuoteOpen(false);
    };
    
    const handleSubmitShareQuote = async ({ email, mobile }) => {
        if (selectedPlansForShare.length < 1) {
            toast.error("Please select at least 1 plan.");
            return;
        }
        setShareQuoteSubmitting(true);
        try {
            const coverageDetailsPayload = selectedPlansForShare.map(buildCoveragePricingDetails);
            const payload = {
                plan_id: sharePlan?.id || null,
                enquiry_data: enquiryData ? JSON.stringify(enquiryData) : null,
                plan_details: selectedPlansForShare.length > 0 ? JSON.stringify(selectedPlansForShare) : null,
                mobile_no: mobile || null,
                email: email || null,
                coverage_pricing_details:
                    coverageDetailsPayload.length > 0
                        ? JSON.stringify(coverageDetailsPayload)
                        : null,
            };
            await savePremiumCalculatorEnquiry(payload);
            if (isMountedRef.current) {
                setIsShareQuoteOpen(false);
            }
            toast.success("Quote shared successfully!");
            onShareQuoteSuccess();
        } catch (error) {
            console.error("Unable to submit share quote:", error);
        } finally {
            if (isMountedRef.current) {
                setShareQuoteSubmitting(false);
            }
        }
    };

    /* ─────────────────────────────── RENDER ─────────────────────────────── */
    return (
        <>
            {isLoadingPlans ? (
                /* ── loader ── */
                <div
                    style={{
                        minHeight: "260px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                    }}
                >
                    <img
                        src={planLoader}
                        alt="Loading plans"
                        style={{ maxWidth: "220px", width: "100%", height: "auto" }}
                    />
                </div>
            ) : (
                <>
                    {/* ── toolbar: back / search / share / pagination ── */}
                    {(plans.length > 0 || planCount > 0 || Boolean(activeSearchQuery)) && (
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: "12px",
                                marginBottom: "20px",
                            }}
                        >
                            {showBackButton && (
                                <Button
                                    color="secondary"
                                    onClick={onBack}
                                    style={{ flexShrink: 0 }}
                                >
                                    &larr; Back
                                </Button>
                            )}

                            {/* search */}
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                    flexWrap: "wrap",
                                    justifyContent: "center",
                                    flex: 1,
                                    minWidth: "260px",
                                }}
                            >
                                <Input
                                    type="text"
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            handleSearch();
                                        }
                                    }}
                                    placeholder="Search by plan name..."
                                    style={{
                                        maxWidth: "320px",
                                        borderRadius: "999px",
                                        border: "1px solid #cbd5e1",
                                        paddingLeft: "14px",
                                        paddingRight: "14px",
                                    }}
                                />
                                <Button
                                    color="primary"
                                    onClick={handleSearch}
                                    style={{
                                        borderRadius: "999px",
                                        backgroundColor: "#0052a3",
                                        borderColor: "#0052a3",
                                        fontWeight: 600,
                                        padding: "6px 16px",
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = "#003d7a";
                                        e.currentTarget.style.borderColor = "#003d7a";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = "#0052a3";
                                        e.currentTarget.style.borderColor = "#0052a3";
                                    }}
                                >
                                    Search
                                </Button>
                            </div>

                            {/* share + pagination */}
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                    gap: "12px",
                                }}
                            >
                                <Button
                                    color="primary"
                                    onClick={handleOpenShareQuote}
                                    disabled={selectedPlansForShare.length < 1}
                                    style={{
                                        borderRadius: "999px",
                                        backgroundColor:
                                            selectedPlansForShare.length < 1 ? "#93c5fd" : "#0052a3",
                                        borderColor:
                                            selectedPlansForShare.length < 1 ? "#93c5fd" : "#0052a3",
                                        color: "#ffffff",
                                        fontWeight: 600,
                                        padding: "6px 16px",
                                        transition: "all 0.2s ease",
                                    }}
                                    onMouseEnter={(e) => {
                                        if (selectedPlansForShare.length < 1) return;
                                        e.currentTarget.style.backgroundColor = "#003d7a";
                                        e.currentTarget.style.borderColor = "#003d7a";
                                    }}
                                    onMouseLeave={(e) => {
                                        if (selectedPlansForShare.length < 1) return;
                                        e.currentTarget.style.backgroundColor = "#0052a3";
                                        e.currentTarget.style.borderColor = "#0052a3";
                                    }}
                                >
                                    Share Quote ({selectedPlansForShare.length}/3)
                                </Button>

                                {planCount > plansPerPage && (
                                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                        <Button
                                            color="link"
                                            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                                            disabled={currentPage === 1}
                                            style={{
                                                borderRadius: "50%",
                                                width: "40px",
                                                height: "40px",
                                                padding: 0,
                                                backgroundColor: currentPage === 1 ? "#e8eef7" : "#ffffff",
                                                border: currentPage === 1 ? "2px solid #cbd5e1" : "2px solid #0052a3",
                                                color: currentPage === 1 ? "#a0aec0" : "#0052a3",
                                                cursor: currentPage === 1 ? "not-allowed" : "pointer",
                                                transition: "all 0.3s ease",
                                                opacity: currentPage === 1 ? 0.6 : 1,
                                            }}
                                            onMouseEnter={(e) => {
                                                if (currentPage > 1) {
                                                    e.currentTarget.style.backgroundColor = "#e3f2fd";
                                                    e.currentTarget.style.transform = "scale(1.05)";
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (currentPage > 1) {
                                                    e.currentTarget.style.backgroundColor = "#ffffff";
                                                    e.currentTarget.style.transform = "scale(1)";
                                                }
                                            }}
                                        >
                                            <i className="fa fa-chevron-left" />
                                        </Button>
                                        <span
                                            style={{
                                                color: "#0052a3",
                                                fontSize: "13px",
                                                fontWeight: 600,
                                                minWidth: "80px",
                                                textAlign: "center",
                                            }}
                                        >
                                            Page {currentPage} – {totalPages}
                                        </span>
                                        <Button
                                            color="link"
                                            onClick={() =>
                                                onPageChange(Math.min(totalPages, currentPage + 1))
                                            }
                                            disabled={currentPage === totalPages}
                                            style={{
                                                borderRadius: "50%",
                                                width: "40px",
                                                height: "40px",
                                                padding: 0,
                                                backgroundColor:
                                                    currentPage === totalPages ? "#dbeafe" : "#0052a3",
                                                border: "2px solid #0052a3",
                                                color:
                                                    currentPage === totalPages ? "#64748b" : "#ffffff",
                                                cursor:
                                                    currentPage === totalPages
                                                        ? "not-allowed"
                                                        : "pointer",
                                                transition: "all 0.3s ease",
                                                opacity: currentPage === totalPages ? 0.7 : 1,
                                            }}
                                            onMouseEnter={(e) => {
                                                if (currentPage < totalPages) {
                                                    e.currentTarget.style.backgroundColor = "#003d7a";
                                                    e.currentTarget.style.transform = "scale(1.05)";
                                                    e.currentTarget.style.boxShadow =
                                                        "0 4px 12px rgba(0,82,163,0.3)";
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (currentPage < totalPages) {
                                                    e.currentTarget.style.backgroundColor = "#0052a3";
                                                    e.currentTarget.style.transform = "scale(1)";
                                                    e.currentTarget.style.boxShadow = "none";
                                                }
                                            }}
                                        >
                                            <i className="fa fa-chevron-right" />
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* ── plan cards grid ── */}
                    <div className="row">
                        {plans.map((plan) => {
                            const isSelected = selectedPlanIds.includes(plan.id);
                            const isPrimarySelected = selectedPlanDetails?.plan_id === plan.id;

                            /* plan cost from selection state if selected, otherwise first cover */
                            const planCost = isPrimarySelected
                                ? toTwo(
                                      selectedPlanDetails?.basic_cost ||
                                          selectedPlanDetails?.cost ||
                                          0
                                  )
                                : toTwo(plan.plan_costs?.[0]?.cost || 0);

                            const currentCover = isPrimarySelected
                                ? selectedPlanDetails?.cover ||
                                  plan.plan_costs?.[0]?.cover ||
                                  ""
                                : plan.plan_costs?.[0]?.cover || "";

                            /* simplified pricing: no addons, no discount */
                            const gstAmount = toTwo(planCost * 0.18);
                            const totalCost = toTwo(planCost + gstAmount);

                            const currencySymbol = getCoverageSymbol(plan, currentCover);

                            const coverageRows = buildCoveragePricingDetails(plan).coverages;

                            return (
                                <Col md="3" key={plan.id} style={{ marginBottom: "24px" }}>
                                    <div
                                        className={`pricingtable ${isSelected ? "selected" : ""}`}
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            borderRadius: "16px",
                                            overflow: "hidden",
                                            boxShadow: isSelected
                                                ? "0 6px 24px rgba(0,82,163,0.18)"
                                                : "0 2px 10px rgba(0,0,0,0.07)",
                                            border: isSelected
                                                ? "2px solid #0052a3"
                                                : "1px solid #e2e8f0",
                                            transition: "box-shadow 0.2s, border-color 0.2s",
                                            backgroundColor: "#ffffff",
                                        }}
                                    >
                                        {/* ── plan name header ── */}
                                        <div
                                            style={{
                                                background: isSelected
                                                    ? "linear-gradient(135deg, #0052a3 0%, #0070d8 100%)"
                                                    : "linear-gradient(135deg, #1e3a5f 0%, #0052a3 100%)",
                                                padding: "16px 18px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                gap: "10px",
                                            }}
                                        >
                                            <span
                                                style={{
                                                    color: "#ffffff",
                                                    fontWeight: 700,
                                                    fontSize: "15px",
                                                    lineHeight: 1.3,
                                                    flex: 1,
                                                }}
                                            >
                                                {plan.title}
                                            </span>
                                            <i
                                                className="fa fa-info-circle"
                                                title="View Insurance Benefits"
                                                style={{
                                                    cursor: "pointer",
                                                    fontSize: "18px",
                                                    color: "rgba(255,255,255,0.85)",
                                                    flexShrink: 0,
                                                    transition: "all 0.2s ease",
                                                }}
                                                onClick={() => handleViewInsuranceBenefits(plan)}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.color = "#ffffff";
                                                    e.currentTarget.style.transform = "scale(1.15)";
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.color = "rgba(255,255,255,0.85)";
                                                    e.currentTarget.style.transform = "scale(1)";
                                                }}
                                            />
                                        </div>

                                        {/* ── coverage pricing rows ── */}
                                        <div style={{ flex: 1, padding: "0 0 4px" }}>
                                            {/* column headers */}
                                            <div
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    padding: "8px 18px 6px",
                                                    backgroundColor: "#f0f6ff",
                                                    borderBottom: "1px solid #dbeafe",
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        fontSize: "11px",
                                                        fontWeight: 700,
                                                        color: "#0052a3",
                                                        textTransform: "uppercase",
                                                        letterSpacing: "0.5px",
                                                    }}
                                                >
                                                    Coverage ({currencySymbol})
                                                </span>
                                                <span
                                                    style={{
                                                        fontSize: "11px",
                                                        fontWeight: 700,
                                                        color: "#0052a3",
                                                        textTransform: "uppercase",
                                                        letterSpacing: "0.5px",
                                                    }}
                                                >
                                                    Membership Fee (incl. GST)
                                                </span>
                                            </div>

                                            {/* rows */}
                                            {coverageRows.map((row, idx) => (
                                                (() => {
                                                    const rowCoverageSymbol = getCoverageSymbol(plan, row.cover);

                                                    return (
                                                        <div
                                                            key={idx}
                                                            style={{
                                                                display: "flex",
                                                                justifyContent: "space-between",
                                                                alignItems: "center",
                                                                padding: "8px 18px",
                                                                borderBottom:
                                                                    idx < coverageRows.length - 1
                                                                        ? "1px solid #f1f5f9"
                                                                        : "none",
                                                                backgroundColor:
                                                                    idx % 2 === 0 ? "#ffffff" : "#fafcff",
                                                            }}
                                                        >
                                                            <span
                                                                style={{
                                                                    fontSize: "13px",
                                                                    color: "#334155",
                                                                    fontWeight: 500,
                                                                }}
                                                            >
                                                                {rowCoverageSymbol}
                                                                {Number(row.cover).toLocaleString("en-IN")}
                                                            </span>
                                                            <span
                                                                style={{
                                                                    fontSize: "13px",
                                                                    fontWeight: 700,
                                                                    color: "#0052a3",
                                                                }}
                                                            >
                                                                ₹{row.plan_cost_with_gst.toFixed(2)}
                                                            </span>
                                                        </div>
                                                    );
                                                })()
                                            ))}

                                            {coverageRows.length === 0 && (
                                                <div
                                                    style={{
                                                        padding: "16px 18px",
                                                        color: "#94a3b8",
                                                        fontSize: "13px",
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    No coverage data available
                                                </div>
                                            )}
                                        </div>

                                        {/* ── GST note ── */}
                                        <div
                                            style={{
                                                padding: "6px 18px",
                                                backgroundColor: "#f8fafc",
                                                borderTop: "1px solid #e2e8f0",
                                            }}
                                        >
                                            <span
                                                style={{
                                                    fontSize: "11px",
                                                    color: "#94a3b8",
                                                }}
                                            >
                                                * Membership fee inclusive of 18% GST
                                            </span>
                                        </div>

                                        {/* ── select button ── */}
                                        <div
                                            className="pricingtable-signup"
                                            style={{ padding: "12px 18px" }}
                                        >
                                            <Button
                                                color={isSelected ? "danger" : "primary"}
                                                onClick={() =>
                                                    handleTogglePlanSelection(
                                                        plan,
                                                        currentCover,
                                                        totalCost,
                                                        planCost,
                                                    )
                                                }
                                                style={{
                                                    width: "100%",
                                                    borderRadius: "8px",
                                                    fontWeight: 600,
                                                    fontSize: "14px",
                                                    padding: "9px",
                                                    backgroundColor: isSelected ? "#dc2626" : "#0052a3",
                                                    borderColor: isSelected ? "#dc2626" : "#0052a3",
                                                    transition: "background-color 0.2s, border-color 0.2s",
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.backgroundColor = isSelected
                                                        ? "#b91c1c"
                                                        : "#003d7a";
                                                    e.currentTarget.style.borderColor = isSelected
                                                        ? "#b91c1c"
                                                        : "#003d7a";
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor = isSelected
                                                        ? "#dc2626"
                                                        : "#0052a3";
                                                    e.currentTarget.style.borderColor = isSelected
                                                        ? "#dc2626"
                                                        : "#0052a3";
                                                }}
                                            >
                                                {isSelected ? "✕ Deselect" : "Select Plan"}
                                            </Button>
                                        </div>
                                    </div>
                                </Col>
                            );
                        })}
                    </div>
                </>
            )}

            <ShareQuoteModal
                isOpen={isShareQuoteOpen}
                toggle={handleCloseShareQuote}
                onSubmit={handleSubmitShareQuote}
                isSubmitting={shareQuoteSubmitting}
            />

            {isViewInsuranceBenefit && (
                <InsuranceBenefit
                    isOpen={isViewInsuranceBenefit}
                    toggle={() => setViewInsuranceBenefit(false)}
                    insuranceBenefits={insuranceBenefitsData}
                    type={enquiryData?.scheme?.label || ''}
                />
            )}
        </>
    );
};

export default MembershipFee;
